import { defaultSessionConfig, reportMessages } from "@studystraight/shared-config";
import type {
  PostureIssueType,
  SessionIssueCounters,
  StudySession,
} from "@studystraight/shared-types";

import { formatDay, formatMinutes, formatTime } from "../../app/format.js";

/** Thứ tự hiển thị = thứ tự ưu tiên khi nhắc (§21), để phụ huynh đọc thấy nhất quán với app. */
const ISSUE_ORDER: readonly PostureIssueType[] = [
  "TOO_CLOSE",
  "HEAD_DOWN",
  "LEANING_FORWARD",
  "SHOULDER_TILT",
];

const COUNTER_KEY: Record<PostureIssueType, keyof SessionIssueCounters> = {
  HEAD_DOWN: "headDown",
  LEANING_FORWARD: "leaningForward",
  TOO_CLOSE: "tooClose",
  SHOULDER_TILT: "shoulderTilt",
};

export interface ReportScreenProps {
  session: StudySession;
  onHome: () => void;
  onHistory: () => void;
}

/** §26 — báo cáo sau phiên học. Chỉ số liệu; §7 nhắc lại rằng không có video nào được lưu. */
export function ReportScreen({ session, onHome, onHistory }: ReportScreenProps): React.JSX.Element {
  const tooShort = session.duration < defaultSessionConfig.minReportableDurationMs;
  const excluded = new Set(session.excludedIssues);

  return (
    <main className="screen">
      <header className="topbar">
        <h2 className="topbar__title">{reportMessages.title}</h2>
        <span className="muted topbar__end">
          {formatDay(session.startTime)} · {formatTime(session.startTime)}
        </span>
      </header>

      <div className="report">
        {tooShort ? <p className="hint">{reportMessages.tooShort}</p> : null}

        <div className="report__grid">
          <div className="stat">
            <span className="stat__value">{formatMinutes(session.duration)}</span>
            <span className="stat__label">{reportMessages.duration}</span>
          </div>
          <div className="stat">
            <span className="stat__value">{session.goodPosturePercentage}%</span>
            <span className="stat__label">{reportMessages.goodPosture}</span>
          </div>
          <div className="stat">
            <span className="stat__value">{session.averagePostureScore}</span>
            <span className="stat__label">{reportMessages.averageScore}</span>
          </div>
          <div className="stat">
            <span className="stat__value">{session.alertCount}</span>
            <span className="stat__label">{reportMessages.alerts}</span>
          </div>
        </div>

        {/* Đường điểm dựng từ `scoreSamples` (1 điểm/30s) — đủ để thấy phiên xấu đi lúc nào mà
            không phải lưu từng frame (§22). */}
        {session.scoreSamples.length > 1 ? (
          <div className="spark" role="img" aria-label="Diễn biến điểm tư thế theo thời gian">
            {session.scoreSamples.map((sample) => (
              <span
                key={sample.atMs}
                className="spark__bar"
                style={{ height: `${Math.max(4, Math.min(100, sample.score))}%` }}
              />
            ))}
          </div>
        ) : null}

        <section>
          <h3 className="section-title">{reportMessages.issuesTitle}</h3>
          <dl className="rows">
            {ISSUE_ORDER.map((issue) => (
              <div className="rows__row" key={issue}>
                <dt>{reportMessages.issueLabels[issue]}</dt>
                {/* Edge case #15/#16: "không đo được" KHÁC "0 lần". Hiện số 0 cho một mục mà chế độ
                    đặt máy không đo được là nói với phụ huynh một điều ta không hề biết. */}
                <dd className={excluded.has(issue) ? "muted" : undefined}>
                  {excluded.has(issue)
                    ? reportMessages.notMeasured
                    : `${session.issues[COUNTER_KEY[issue]]} ${reportMessages.times}`}
                </dd>
              </div>
            ))}
            <div className="rows__row">
              <dt>{reportMessages.eyeBreaks}</dt>
              <dd>
                {session.eyeBreakCount} {reportMessages.times}
              </dd>
            </div>
          </dl>
        </section>

        <p className="privacy">{reportMessages.noVideo}</p>

        <div className="actions">
          <button type="button" className="btn btn--secondary" onClick={onHistory}>
            Lịch sử
          </button>
          <button type="button" className="btn btn--primary" onClick={onHome}>
            {reportMessages.backHome}
          </button>
        </div>
      </div>
    </main>
  );
}
